// Components/ChatComponents/BubbleManagement/BubbleOrganizerSidebar.jsx
// UPDATED: Added member sharing system

import React, { useState, useCallback, memo } from 'react';
import {
  Box, Typography, IconButton, Button, TextField,
  Switch, Alert, CircularProgress, Chip, Stack,
  Collapse, Tooltip, Fade, Zoom, Dialog, DialogTitle,
  DialogContent, DialogActions, List, ListItem,
  FormControlLabel, Checkbox, Avatar, Divider,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Folder as FolderIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  OpenInNew as OpenIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Description as DocumentIcon,
  InsertDriveFile as FileIcon,
  Lock as LockIcon,
  PersonAdd as PersonAddIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useMutation, useQuery } from 'react-query';
import API from '../API';
import { useNavigate } from 'react-router-dom';
import routes from '../Config/routes';

/* ───────────────────────── Styled Components ───────────────────────── */
const Sidebar = styled(Box)(({ theme, open }) => ({
  width: '100%',
  height: '100%',
  background: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'visible',
}));

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
}));

const OrganizerItem = styled(Box)(({ theme, isPrimary, isEditing }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${isPrimary ? theme.palette.primary.main : 'transparent'}`,
  background: isEditing 
    ? alpha(theme.palette.primary.main, 0.04)
    : alpha(theme.palette.background.paper, 0.6),
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  '&:hover': {
    background: alpha(theme.palette.background.paper, 1),
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
    transform: 'translateY(-1px)',
  },
}));

const FileItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1),
  borderRadius: theme.spacing(0.75),
  transition: 'background-color 0.15s',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  },
}));

const QuickAction = styled(IconButton)(({ theme }) => ({
  opacity: 0,
  transition: 'opacity 0.2s',
  '.organizer-item:hover &': {
    opacity: 1,
  },
}));

/* ───────────────────────── Sub Components ───────────────────────── */
const FileIconDisplay = memo(({ filename }) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  const iconProps = { fontSize: 'small', sx: { fontSize: 16 } };
  
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) 
    return <ImageIcon {...iconProps} color="primary" />;
  if (['mp4', 'mov', 'avi'].includes(ext)) 
    return <VideoIcon {...iconProps} color="secondary" />;
  if (['pdf', 'doc', 'docx'].includes(ext)) 
    return <DocumentIcon {...iconProps} color="info" />;
  return <FileIcon {...iconProps} />;
});

const CreateForm = memo(({ onSubmit, onCancel, isLoading }) => {
  const [name, setName] = useState('');
  const [autoShare, setAutoShare] = useState(true);

  const handleSubmit = () => {
    if (name.trim()) {
      onSubmit({ name: name.trim(), auto_share_with_members: autoShare });
    }
  };

  return (
    <Fade in>
      <Box sx={{ p: 2, background: (t) => alpha(t.palette.background.paper, 0.6), borderRadius: 2, mb: 2 }}>
        <Stack spacing={2}>
          <TextField
            size="small"
            placeholder="Organizer name..."
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
          />
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <Switch 
                size="small" 
                checked={autoShare} 
                onChange={(e) => setAutoShare(e.target.checked)} 
              />
              <Typography variant="caption" color="text.secondary">
                Auto-share with members
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button size="small" onClick={onCancel}>Cancel</Button>
              <Button 
                size="small" 
                variant="contained" 
                onClick={handleSubmit}
                disabled={!name.trim() || isLoading}
              >
                {isLoading ? <CircularProgress size={16} /> : 'Create'}
              </Button>
            </Box>
          </Box>
        </Stack>
      </Box>
    </Fade>
  );
});

const SettingsPanel = memo(({ organizer, onSave, onCancel, isLoading }) => {
  const [settings, setSettings] = useState({
    display_name: organizer.display_name || '',
    member_can_upload: organizer.member_can_upload ?? true,
    member_can_organize: organizer.member_can_organize ?? false,
    admin_only: organizer.admin_only ?? false,
    visible_in_feed: organizer.visible_in_feed ?? true,
    is_primary: organizer.is_primary ?? false,
  });

  const Setting = ({ label, value, onChange, description }) => (
    <Box display="flex" alignItems="center" justifyContent="space-between" py={0.5}>
      <Box flex={1}>
        <Typography variant="caption" fontWeight={600} display="block">
          {label}
        </Typography>
        {description && (
          <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
            {description}
          </Typography>
        )}
      </Box>
      <Switch size="small" checked={value} onChange={onChange} />
    </Box>
  );

  return (
    <Collapse in>
      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Display name (optional)"
          value={settings.display_name}
          onChange={(e) => setSettings({ ...settings, display_name: e.target.value })}
          sx={{ mb: 2 }}
        />
        
        <Stack spacing={1}>
          <Setting
            label="Members Can Upload"
            description="All members can upload files"
            value={settings.member_can_upload}
            onChange={(e) => setSettings({ ...settings, member_can_upload: e.target.checked })}
          />
          <Setting
            label="Members Can Organize"
            value={settings.member_can_organize}
            onChange={(e) => setSettings({ ...settings, member_can_organize: e.target.checked })}
          />
          <Setting
            label="Admin Only"
            description="Restrict to admins"
            value={settings.admin_only}
            onChange={(e) => setSettings({ ...settings, admin_only: e.target.checked })}
          />
          <Setting
            label="Visible in Feed"
            value={settings.visible_in_feed}
            onChange={(e) => setSettings({ ...settings, visible_in_feed: e.target.checked })}
          />
          <Setting
            label="Primary Organizer"
            value={settings.is_primary}
            onChange={(e) => setSettings({ ...settings, is_primary: e.target.checked })}
          />
        </Stack>

        <Box display="flex" gap={1} mt={2}>
          <Button size="small" onClick={onCancel} fullWidth>Cancel</Button>
          <Button 
            size="small" 
            variant="contained" 
            onClick={() => onSave(settings)}
            disabled={isLoading}
            fullWidth
          >
            {isLoading ? <CircularProgress size={16} /> : 'Save'}
          </Button>
        </Box>
      </Box>
    </Collapse>
  );
});

/* ───────────────────────── Share Dialog ───────────────────────── */
const ShareDialog = memo(({ open, onClose, bubbleId, organizerId, members, onSuccess }) => {
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [permissionLevel, setPermissionLevel] = useState('read');
  const [shareMessage, setShareMessage] = useState('');

  const shareMutation = useMutation(
    (data) => API.bubbles.shareOrganizerWithMembers(bubbleId, organizerId, data),
    {
      onSuccess: () => {
        onSuccess?.();
        handleClose();
      },
      onError: (err) => {
        alert(err?.response?.data?.error || 'Failed to share organizer');
      },
    }
  );

  const handleClose = () => {
    setSelectedMembers([]);
    setPermissionLevel('read');
    setShareMessage('');
    onClose();
  };

  const handleShare = () => {
    if (selectedMembers.length === 0) {
      alert('Please select at least one member');
      return;
    }

    shareMutation.mutate({
      member_ids: selectedMembers,
      permission_level: permissionLevel,
      share_message: shareMessage.trim() || undefined,
    });
  };

  const toggleMember = (memberId) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share Organizer with Members</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select members to share this organizer with
        </Typography>

        <List sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
          {members.map((member) => (
            <ListItem key={member.id} disablePadding>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedMembers.includes(member.id)}
                    onChange={() => toggleMember(member.id)}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar src={member.pfphref} sx={{ width: 32, height: 32 }}>
                      {(member.display_name || member.username)?.[0]}
                    </Avatar>
                    <Typography variant="body2">
                      {member.display_name || member.username}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Permission Level</InputLabel>
          <Select
            value={permissionLevel}
            onChange={(e) => setPermissionLevel(e.target.value)}
            label="Permission Level"
          >
            <MenuItem value="read">Read Only</MenuItem>
            <MenuItem value="write">Can Upload Files</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={2}
          label="Message (optional)"
          value={shareMessage}
          onChange={(e) => setShareMessage(e.target.value)}
          placeholder="Add a message to members..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleShare}
          disabled={selectedMembers.length === 0 || shareMutation.isLoading}
          startIcon={shareMutation.isLoading ? <CircularProgress size={16} /> : <ShareIcon />}
        >
          Share
        </Button>
      </DialogActions>
    </Dialog>
  );
});

/* ───────────────────────── Main Component ───────────────────────── */
export default function BubbleOrganizerSidebar({
  bubbleId,
  organizers,
  open,
  onClose,
  onRefresh,
  canManage,
  bubble,
}) {
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedOrganizerForShare, setSelectedOrganizerForShare] = useState(null);
  const [snack, setSnack] = useState(null);
  const navigate = useNavigate();

  const canCreateOrganizers = canManage || bubble?.allow_participant_organizers === true;

  // Fetch bubble members for sharing
  const { data: membersData } = useQuery(
    ['bubble-members', bubbleId],
    () => API.bubbles.getConversationParticipants(bubbleId),
    {
      enabled: canManage && shareDialogOpen,
    }
  );

  const bubbleMembers = membersData?.participants || [];

  /* ───────────────────────── Mutations ───────────────────────── */
  const createMutation = useMutation(
    (data) => API.bubbles.createOrganizer(bubbleId, data),
    {
      onSuccess: () => {
        onRefresh?.();
        setCreating(false);
        setSnack({ msg: 'Created successfully', sev: 'success' });
      },
      onError: (e) => setSnack({ msg: e?.response?.data?.error || 'Failed to create', sev: 'error' }),
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => API.bubbles.updateOrganizerSettings(bubbleId, id, data),
    {
      onSuccess: () => {
        onRefresh?.();
        setEditingId(null);
        setSnack({ msg: 'Updated successfully', sev: 'success' });
      },
      onError: (e) => setSnack({ msg: e?.response?.data?.error || 'Failed to update', sev: 'error' }),
    }
  );

  const deleteMutation = useMutation(
    (id) => API.bubbles.removeOrganizer(bubbleId, id),
    {
      onSuccess: () => {
        onRefresh?.();
        setDeletingId(null);
        setSnack({ msg: 'Removed successfully', sev: 'success' });
      },
      onError: (e) => setSnack({ msg: e?.response?.data?.error || 'Failed to remove', sev: 'error' }),
    }
  );

  /* ───────────────────────── Handlers ───────────────────────── */
  const handleCreate = useCallback((data) => {
    createMutation.mutate({ ...data, member_can_upload: true, member_can_organize: false });
  }, [createMutation]);

  const handleUpdate = useCallback((id, data) => {
    updateMutation.mutate({ id, data });
  }, [updateMutation]);

  const handleOpenShareDialog = (organizer) => {
    setSelectedOrganizerForShare(organizer);
    setShareDialogOpen(true);
  };

  const handleShareSuccess = () => {
    setSnack({ msg: 'Organizer shared successfully', sev: 'success' });
    onRefresh?.();
  };

  return (
    <Sidebar open={open}>
      <Header>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Organizers
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {organizers.length} total
          </Typography>
        </Box>
        <Box display="flex" gap={0.5}>
          {canCreateOrganizers && !creating && (
            <Tooltip title="Create Organizer">
              <IconButton size="small" onClick={() => setCreating(true)}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Header>

      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        p: 2,
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.2),
          borderRadius: 3,
        },
      }}>
        {!canCreateOrganizers && organizers.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<LockIcon />}>
            <Typography variant="caption">
              Only admins can create organizers in this bubble. You can view existing organizers and upload files.
            </Typography>
          </Alert>
        )}

        {creating && (
          <CreateForm
            onSubmit={handleCreate}
            onCancel={() => setCreating(false)}
            isLoading={createMutation.isLoading}
          />
        )}

        {organizers.length === 0 && !creating ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <FolderIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1, opacity: 0.3 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              No organizers yet
            </Typography>
            {canCreateOrganizers ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCreating(true)}
                sx={{ mt: 2 }}
              >
                Create Organizer
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Only admins can create organizers
              </Typography>
            )}
          </Box>
        ) : (
          <Stack spacing={1}>
            {organizers.map((org) => (
              <OrganizerItem
                key={org.organizer_id}
                className="organizer-item"
                isPrimary={org.is_primary}
                isEditing={editingId === org.organizer_id}
                onClick={() => setExpandedId(expandedId === org.organizer_id ? null : org.organizer_id)}
              >
                <Box display="flex" gap={1.5} alignItems="flex-start">
                  <FolderIcon sx={{ fontSize: 20, mt: 0.25, color: 'primary.main', opacity: 0.7 }} />
                  
                  <Box flex={1} minWidth={0}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {org.display_name || org.organizer_name}
                      </Typography>
                      {org.is_primary && (
                        <Chip 
                          size="small" 
                          label="Primary" 
                          color="primary" 
                          sx={{ height: 16, fontSize: '0.6rem', px: 0.5 }} 
                        />
                      )}
                    </Box>
                    
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      {org.file_count || 0} files
                    </Typography>

                    {(org.member_can_upload || org.admin_only) && (
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {org.member_can_upload && (
                          <Typography variant="caption" sx={{ 
                            px: 0.75, 
                            py: 0.25, 
                            borderRadius: 0.5, 
                            bgcolor: (t) => alpha(t.palette.success.main, 0.1),
                            color: 'success.dark',
                            fontSize: '0.65rem'
                          }}>
                            Upload
                          </Typography>
                        )}
                        {org.admin_only && (
                          <Typography variant="caption" sx={{ 
                            px: 0.75, 
                            py: 0.25, 
                            borderRadius: 0.5, 
                            bgcolor: (t) => alpha(t.palette.warning.main, 0.1),
                            color: 'warning.dark',
                            fontSize: '0.65rem'
                          }}>
                            Admin Only
                          </Typography>
                        )}
                      </Box>
                    )}

                    {org.files?.length > 0 && (
                      <Collapse in={expandedId === org.organizer_id}>
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={1}>
                            Recent Files
                          </Typography>
                          <Stack spacing={0.5}>
                            {org.files.slice(0, 4).map((file, idx) => (
                              <FileItem key={idx}>
                                <FileIconDisplay filename={file.filename} />
                                <Typography variant="caption" noWrap flex={1}>
                                  {file.filename}
                                </Typography>
                              </FileItem>
                            ))}
                            {org.files.length > 4 && (
                              <Typography variant="caption" color="text.secondary" sx={{ pl: 1, pt: 0.5 }}>
                                +{org.files.length - 4} more
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      </Collapse>
                    )}

                    {editingId === org.organizer_id && (
                      <SettingsPanel
                        organizer={org}
                        onSave={(data) => handleUpdate(org.organizer_id, data)}
                        onCancel={() => setEditingId(null)}
                        isLoading={updateMutation.isLoading}
                      />
                    )}
                  </Box>

                  {canManage && editingId !== org.organizer_id && (
                    <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                      <QuickAction 
                        size="small" 
                        onClick={() => handleOpenShareDialog(org)}
                        title="Share with members"
                      >
                        <PersonAddIcon sx={{ fontSize: 16 }} />
                      </QuickAction>
                      <QuickAction 
                        size="small" 
                        onClick={() => navigate(`/organizers/${org.organizer_id}`)}
                      >
                        <OpenIcon sx={{ fontSize: 16 }} />
                      </QuickAction>
                      <QuickAction 
                        size="small" 
                        onClick={() => setEditingId(org.organizer_id)}
                      >
                        <SettingsIcon sx={{ fontSize: 16 }} />
                      </QuickAction>
                      {deletingId === org.organizer_id ? (
                        <QuickAction 
                          size="small" 
                          color="error"
                          onClick={() => deleteMutation.mutate(org.organizer_id)}
                        >
                          <CheckIcon sx={{ fontSize: 16 }} />
                        </QuickAction>
                      ) : (
                        <QuickAction 
                          size="small" 
                          onClick={() => setDeletingId(org.organizer_id)}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </QuickAction>
                      )}
                    </Box>
                  )}
                </Box>
              </OrganizerItem>
            ))}
          </Stack>
        )}
      </Box>

      {/* Share Dialog */}
      {selectedOrganizerForShare && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
            setSelectedOrganizerForShare(null);
          }}
          bubbleId={bubbleId}
          organizerId={selectedOrganizerForShare.organizer_id}
          members={bubbleMembers}
          onSuccess={handleShareSuccess}
        />
      )}

      {/* Snackbar */}
      {snack && (
        <Zoom in>
          <Alert
            severity={snack.sev}
            onClose={() => setSnack(null)}
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              right: 16,
              boxShadow: 3,
            }}
          >
            {snack.msg}
          </Alert>
        </Zoom>
      )}
    </Sidebar>
  );
}